import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';

interface TextareaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onSave: (value: string) => void;
  title?: string;
  placeholder?: string;
  maxLength?: number;
}

export function TextareaDialog({
  isOpen,
  onClose,
  value,
  onSave,
  title = "Edit Text",
  placeholder = "Enter text...",
  maxLength = 1000
}: TextareaDialogProps) {
  const [editValue, setEditValue] = useState(value);

  // Reset the edit value when the dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setEditValue(value);
    }
  }, [isOpen, value]);

  const handleSave = () => {
    onSave(editValue);
    onClose();
  };

  const handleCancel = () => {
    setEditValue(value); // Reset to original value
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 min-h-0">
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full h-full min-h-[300px] resize-none"
            maxLength={maxLength}
            autoFocus
          />
          
          {maxLength && (
            <div className="text-xs text-muted-foreground mt-1 text-right">
              {editValue.length} / {maxLength} characters
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center">
          <div className="text-xs text-muted-foreground">
            Press Ctrl+Enter to save, Escape to cancel
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 